import { useCallback, useEffect, useState } from 'react';

import {
  type TestWidgetUser,
  getActiveTestUser,
  loadTestUsers,
  makeTestVisitorId,
  saveTestUsers,
  setActiveTestUser,
  subscribeTestWidget,
} from '../helpers/testWidgetUsers';

export const useTestWidgetUsers = (
  businessId: number | undefined,
  employeeId: number | undefined,
) => {
  const [users, setUsers] = useState<TestWidgetUser[]>([]);
  const [activeId, setActiveId] = useState<string | null>(
    () => getActiveTestUser()?.id ?? null,
  );

  const reload = useCallback((): void => {
    setActiveId(getActiveTestUser()?.id ?? null);
    if (businessId && employeeId)
      void loadTestUsers(businessId, employeeId).then(setUsers);
  }, [businessId, employeeId]);

  useEffect(() => {
    reload();
    return subscribeTestWidget(reload);
  }, [reload]);

  const switchUser = useCallback((user: TestWidgetUser): void => {
    setActiveTestUser({ id: user.id, name: user.name });
  }, []);

  const addUser = useCallback(
    async (name: string): Promise<void> => {
      if (!businessId || !employeeId) return;
      const user: TestWidgetUser = {
        id: makeTestVisitorId(employeeId),
        name: name.trim(),
        createdAt: Date.now(),
      };
      const next = [...users, user];
      setUsers(next);
      await saveTestUsers(businessId, employeeId, next);
      setActiveTestUser({ id: user.id, name: user.name });
    },
    [businessId, employeeId, users],
  );

  const deleteUser = useCallback(
    async (user: TestWidgetUser): Promise<void> => {
      if (!businessId || !employeeId) return;
      const next = users.filter(u => u.id !== user.id);
      setUsers(next);
      await saveTestUsers(businessId, employeeId, next);
      if (getActiveTestUser()?.id === user.id)
        setActiveTestUser(
          next.length ? { id: next[0].id, name: next[0].name } : null,
        );
    },
    [businessId, employeeId, users],
  );

  return { users, activeId, addUser, switchUser, deleteUser };
};
